<template>
  <v-dialog
    :value="isOpen"
    @input="$emit('modalToggle', $event)"
    @click:outside="tryClose()"
    @keydown.esc="tryClose()"
    :max-width="1500"
    :content-class="
      mobileView ? 'weekly-time-schedules-modal-mobile' : 'weekly-time-schedules-modal'
    "
    :fullscreen="mobileView"
    persistent
  >
    <v-btn v-if="mobileView && mobileViewDetail" @click="tryGoBackToMenu()" class="back-btn" icon
      ><v-icon>mdi-arrow-left</v-icon></v-btn
    >
    <v-btn @click="tryClose" :color="mobileView ? 'default' : 'white'" class="close-btn" icon
      ><v-icon>mdi-close</v-icon></v-btn
    >
    <v-snackbar
      :value="Boolean(error)"
      color="info"
      height="39"
      :timeout="10000"
      :style="{
        top: mobileView ? undefined : '-70px',
      }"
      :max-width="mobileView ? 'calc(100% - 100px)' : undefined"
      :min-width="mobileView ? 'initial' : undefined"
      top
      absolute
      >{{ error }}</v-snackbar
    >

    <div class="modal-content-wrapper">
      <div v-show="!mobileView || !mobileViewDetail" class="sidebar">
        <h1>{{ $t('global.weeklyTimeSchedules') }}</h1>
        <div class="schedule-list">
          <button
            v-if="operationMode === 'CREATING_NEW' || operationMode === 'CREATING_NEW_AND_ACTIVATE'"
            class="schedule-selector-btn active d-flex flex-row justify-content-between"
          >
            <div>
              <h2>{{ $t('global.newSchedule') }}</h2>
            </div>
            <v-btn
              :title="$t('global.delete')"
              icon
              height="24"
              width="24"
              @click="deleteClickHandler"
            >
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </button>
          <button
            v-for="(weeklyTimeSchedule, index) of weeklyTimeSchedules"
            :key="weeklyTimeSchedule.id"
            @click="tryDirectEditSchedule(index, weeklyTimeSchedule)"
            class="schedule-selector-btn"
            :class="{
              active:
                !mobileView &&
                index === selectedWeeklyScheduleIndex &&
                operationMode === 'EDITING_EXISTING',
              'without-action-btns': !editable,
              'cursor-pointer': editable && (!weeklyTimeSchedule.isGlobal || templateMode),
            }"
          >
            <div class="schedule-selector-btn-texts">
              <h2>{{ weeklyTimeSchedule.name }}</h2>
            </div>
            <div class="active-icon" v-if="weeklyTimeSchedule.id === activeTimeScheduleId">
              <v-icon size="12" color="white">mdi-check</v-icon>
            </div>
            <div
              v-if="editable || canActivate"
              class="action-buttons"
              :class="{ 'always-visible': mobileView }"
            >
              <v-btn
                v-if="editable && !hasUnsavedChanges"
                :title="$t('global.duplicateSchedule')"
                icon
                height="24"
                width="24"
                @click.stop="tryStartCreatingNewSchedule(index)"
                ><v-icon small>mdi-content-duplicate</v-icon></v-btn
              >
              <v-btn
                v-if="
                  editable &&
                  ((!weeklyTimeSchedule.isGlobal &&
                    weeklyTimeSchedule.id !== activeTimeScheduleId) ||
                    (templateMode && !weeklyTimeSchedule.isUsed))
                "
                :title="$t('global.delete')"
                icon
                height="24"
                width="24"
                @click.stop="openDeleteConfirmationModal(index)"
                ><v-icon small>mdi-delete</v-icon></v-btn
              >
              <v-btn
                v-if="
                  canActivate &&
                  weeklyTimeSchedule.id !== activeTimeScheduleId &&
                  !hasUnsavedChanges
                "
                :title="$t('global.activate')"
                icon
                height="24"
                width="24"
                @click.stop="openActivateConfirmation(index)"
                ><v-icon small>mdi-play</v-icon></v-btn
              >
            </div>
          </button>
        </div>
        <div v-if="editable" class="create-btn-wrapper">
          <v-btn v-if="device?.id" @click="resetWeeklySchedules()">{{
            $t('global.resetSchedules')
          }}</v-btn>

          <v-btn outlined depressed @click="tryStartCreatingNewSchedule()" class="mr-2">{{
            $t('global.newWeeklySchedule')
          }}</v-btn>
        </div>
      </div>
      <div v-show="!mobileView || mobileViewDetail" class="weekly-schedule-detail-wrapper">
        <div
          class="d-flex flex-wrap align-center"
          :class="[
            operationMode === 'EDITING_EXISTING' ? 'mb-4' : 'mb-1',
            mobileView ? 'mt-12' : 'mt-8',
            mobileView ? 'mx-2' : 'mx-8',
          ]"
        >
          <v-text-field
            v-model="actualTimeScheduleName"
            :error-messages="visibleTimeScheduleNameErrors"
            :label="$t('global.timeScheduleName')"
            required
            @input="$v.actualTimeScheduleName.$touch()"
            @blur="$v.actualTimeScheduleName.$touch()"
            filled
            class="flex-grow-1 mr-5"
          ></v-text-field>
          <AddDailyScheduleButton
            v-if="editable"
            @select="addDailySchedule"
            @addNew="createNewDailySchedule"
            :actualTimeSchedule="actualTimeSchedule"
            :dailyTimeSchedules="dailyTimeSchedules"
            :btnProps="{
              disabled: !canAddMoreDailySchedules,
              class: 'mb-6',
            }"
          />
        </div>

        <div
          class="daily-schedules"
          :class="{
            'flex-grow-1': actualTimeSchedule.length,
            'mx-8': !mobileView,
            'mx-2': mobileView,
          }"
          ref="sortableDailySchedules"
        >
          <section
            v-for="entry of actualTimeSchedule"
            :key="entry.dailyTimeScheduleId"
            class="daily-entry-wrapper"
            :class="{
              'has-error': !entry.days.length,
              'editable-daily': editable,
            }"
            @click="handleDailyScheduleClick(entry.dailyTimeScheduleId)"
          >
            <div class="entry-header">
              <div class="d-flex align-center">
                <v-icon v-if="editable" class="sort-handle">mdi-drag-vertical</v-icon>
                <h1 class="mr-4">
                  {{ dailyScheduleMap.get(entry.dailyTimeScheduleId)?.name }}
                </h1>
              </div>
              <div class="d-flex align-center">
                <v-menu offset-y :disabled="!editable" :close-on-content-click="false">
                  <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
                    <div
                      v-bind="menuAttrs"
                      v-on="menuOn"
                      :class="{ 'repeat-with-error': !entry.days.length }"
                      :style="{ cursor: editable ? 'pointer' : 'auto' }"
                    >
                      <span class="repeat-span">{{ $t('global.repeat') }}: </span>
                      <span class="repeated-days-list">{{
                        entry.days.length === 7
                          ? $t('global.daily')
                          : entry.days.map((day) => $t(`global.daysOfWeekShort.${day}`)).join(', ')
                      }}</span>
                      <v-icon v-if="editable">mdi-menu-down</v-icon>
                    </div>
                  </template>
                  <v-list>
                    <v-list-item @click="selectAllDays(entry.dailyTimeScheduleId)">
                      {{ $t('global.selectAll') }}
                    </v-list-item>
                    <v-divider class="mt-2"></v-divider>
                    <v-list-item
                      v-for="day of daysOfWeekItems"
                      :key="day.value"
                      class="day-of-week-list-item"
                      @click="computeNextValue(entry.dailyTimeScheduleId, day.value)"
                    >
                      <v-checkbox
                        :input-value="entry.days.includes(day.value)"
                        :label="day.text"
                        color="primary"
                        :ripple="false"
                        hide-details
                        dense
                      ></v-checkbox>
                    </v-list-item>
                  </v-list>
                </v-menu>
                <v-tooltip v-if="editable" top>
                  <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                    <v-btn
                      v-bind="tooltipAttrs"
                      v-on="tooltipOn"
                      @click.stop="duplicateDailySchedule(entry.dailyTimeScheduleId)"
                      icon
                      class="ml-2 mr-n2"
                    >
                      <v-icon>mdi-content-duplicate</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('global.duplicateScheduleTooltip') }}</span>
                </v-tooltip>
                <v-tooltip v-if="editable" top>
                  <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                    <v-btn
                      v-bind="tooltipAttrs"
                      v-on="tooltipOn"
                      @click.stop="removeDailySchedule(entry.dailyTimeScheduleId)"
                      icon
                      class="ml-2 mr-n2"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('global.delete') }}</span>
                </v-tooltip>
              </div>
            </div>
            <DailyScheduleOverview
              v-if="dailyScheduleMap.has(entry.dailyTimeScheduleId)"
              :timeSchedule="dailyScheduleMap.get(entry.dailyTimeScheduleId)"
              :timeScheduleConfig="timeScheduleConfig"
              :deviceType="device?.type"
              @dailyScheduleErrorConditionMet="showErrorModal"
              showCurrentTime
            />
            <v-dialog persistent @click:outside.stop max-width="300px" v-model="isErrorModalOpen">
              <div class="error-modal-content">
                <p>{{ $t('global.errorInTimeScheduleConfiguration') }}</p>
                <v-btn color="primary" @click="closeErrorModalAndRefresh">{{
                  $t('global.close')
                }}</v-btn>
              </div>
            </v-dialog>
          </section>
          <div v-if="actualTimeSchedule.length && editable" class="text-center my-5">
            <AddDailyScheduleButton
              @select="addDailySchedule"
              @addNew="createNewDailySchedule"
              :actualTimeSchedule="actualTimeSchedule"
              :dailyTimeSchedules="dailyTimeSchedules"
            />
          </div>
        </div>
        <div
          v-if="!actualTimeSchedule.length"
          class="d-flex flex-column align-center flex-grow-1 justify-center"
        >
          <v-icon x-large class="mb-5">mdi-close-box-outline</v-icon>
          <p class="text-center">
            <b>{{ $t('global.nothingToSeeHereYet') }}</b
            ><br />{{ $t('global.addFirstDailyScheduleMessage') }}
          </p>
          <AddDailyScheduleButton
            v-if="editable"
            @select="addDailySchedule"
            @addNew="createNewDailySchedule"
            :actualTimeSchedule="actualTimeSchedule"
            :dailyTimeSchedules="dailyTimeSchedules"
            :btnProps="{ color: 'primary', class: 'mb-10' }"
          />
        </div>

        <footer
          :class="[
            mobileView ? 'pa-2' : 'pa-8',
            templateMode ||
            operationMode === 'CREATING_NEW' ||
            operationMode === 'CREATING_NEW_AND_ACTIVATE' ||
            (selectedWeeklyScheduleIndex != null &&
              !weeklyTimeSchedules[selectedWeeklyScheduleIndex]?.isGlobal)
              ? 'justify-space-between'
              : 'justify-end',
          ]"
          v-if="editable || canActivate"
        >
          <v-tooltip
            v-if="
              templateMode ||
              operationMode === 'CREATING_NEW' ||
              operationMode === 'CREATING_NEW_AND_ACTIVATE' ||
              (selectedWeeklyScheduleIndex != null &&
                !weeklyTimeSchedules[selectedWeeklyScheduleIndex]?.isGlobal)
            "
            top
            :disabled="!disabledDeleteMessage"
          >
            <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
              <div v-bind="tooltipAttrs" v-on="tooltipOn">
                <v-btn
                  :disabled="Boolean(disabledDeleteMessage)"
                  color="error"
                  text
                  @click="deleteClickHandler"
                  ><v-icon left>mdi-delete</v-icon>{{ $t('global.delete') }}</v-btn
                >
              </div>
            </template>
            <span>{{ disabledDeleteMessage }}</span>
          </v-tooltip>
          <div class="actions">
            <v-btn
              v-if="
                editable &&
                operationMode === 'EDITING_EXISTING' &&
                selectedWeeklyScheduleIndex != null &&
                weeklyTimeSchedules[selectedWeeklyScheduleIndex] &&
                !weeklyTimeSchedules[selectedWeeklyScheduleIndex].isGlobal &&
                !hasUnsavedChanges
              "
              @click="tryStartCreatingNewSchedule(selectedWeeklyScheduleIndex)"
              depressed
              ><v-icon small class="mr-1">mdi-content-duplicate</v-icon
              >{{ $t('global.duplicateSchedule') }}</v-btn
            >
            <v-btn
              v-if="
                canActivate &&
                operationMode === 'EDITING_EXISTING' &&
                selectedWeeklyScheduleIndex != null &&
                weeklyTimeSchedules[selectedWeeklyScheduleIndex] &&
                weeklyTimeSchedules[selectedWeeklyScheduleIndex].id !== activeTimeScheduleId &&
                !hasUnsavedChanges
              "
              @click="openActivateConfirmation(selectedWeeklyScheduleIndex)"
              color="primary"
              depressed
              >{{ $t('global.activate') }}</v-btn
            >
            <v-btn
              v-if="
                editable &&
                operationMode === 'EDITING_EXISTING' &&
                selectedWeeklyScheduleIndex != null &&
                hasUnsavedChanges
              "
              @click="promptDiscardCurrentChanges"
              color="error"
              text
              depressed
              >{{ $t('global.discardChanges') }}</v-btn
            >
            <v-tooltip :disabled="!disabledSaveMessage || !hasUnsavedChanges" top>
              <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                <div v-bind="tooltipAttrs" v-on="tooltipOn">
                  <v-btn
                    v-if="
                      editable &&
                      canActivate &&
                      operationMode === 'CREATING_NEW_AND_ACTIVATE' &&
                      hasUnsavedChanges
                    "
                    @click="handleSaveAndActivate()"
                    :disabled="Boolean(disabledSaveMessage)"
                    color="primary"
                    depressed
                    >{{ $t('global.saveAndActivate') }}</v-btn
                  >
                  <v-btn
                    v-else-if="
                      editable &&
                      (operationMode === 'EDITING_EXISTING' || operationMode === 'CREATING_NEW') &&
                      hasUnsavedChanges
                    "
                    @click="handleSave()"
                    :disabled="Boolean(disabledSaveMessage)"
                    color="primary"
                    depressed
                    >{{ $t('global.save') }}</v-btn
                  >
                </div>
              </template>
              <span>{{ disabledSaveMessage }}</span>
            </v-tooltip>
          </div>
        </footer>
      </div>
    </div>

    <SystemTimeScheduleEditValidationModal
      v-model="systemTimeScheduleEditValidationModalOpen"
      :duplicateAction="() => tryStartCreatingNewSchedule(selectedWeeklyScheduleIndex)"
    />

    <SystemTimeScheduleEditValidationModal
      v-model="systemDailyTimeScheduleEditValidationModalOpen"
      :duplicateAction="() => editDailySchedule(selectedDailyScheduleId)"
    />

    <DailyTimeScheduleEditModal
      v-if="baseScheduleForDailyEditModal"
      v-model="isEditDailyDialogOpen"
      :timeSchedule="baseScheduleForDailyEditModal"
      :timeScheduleConfig="timeScheduleConfig"
      :saveMethod="dailyEditModalSaveMethod"
      :namesTaken="namesTakenForDailyEditModal"
      :updateMode="dailyEditModalIsInUpdateMode"
      :usedInWeeklySchedulesNames="usedInWeeklyForDailyEditModal"
      :deviceType="device?.type"
    />
    <ConfirmationDialog
      v-model="discardChangesConfirmationModalOpen"
      :heading="$t('device.timeSchedule.editModal.cancelConfirmation.heading')"
      :text="
        $t('device.timeSchedule.editModal.cancelConfirmation.text', {
          scheduleName:
            selectedWeeklyScheduleIndex != null && weeklyTimeSchedules[selectedWeeklyScheduleIndex]
              ? weeklyTimeSchedules[selectedWeeklyScheduleIndex].name
              : $t('global.new'),
        })
      "
      :cancelText="$t('global.continueEditing')"
      :confirmText="$t('global.discardChanges')"
      confirmBtnColor="error"
      :action="discardChangesAction"
    />
    <ConfirmationDialog
      v-model="deleteNewConfirmationModalOpen"
      :heading="$t('global.newWeeklyTimeScheduleDeleteConfirmationHeading')"
      :text="$t('global.newWeeklyTimeScheduleDeleteConfirmationText')"
      :confirmText="$t('global.delete')"
      confirmBtnColor="error"
      :action="
        () => {
          deleteNewConfirmationModalOpen = false
          if (weeklyTimeSchedules.length > 0) {
            switchToSchedule(0)
          } else {
            startCreatingNewSchedule()
          }
          setError($t('global.deleteWeeklyScheduleInfo'))
        }
      "
    />
    <ConfirmationDialog
      v-if="deleteConfirmationModalOpen"
      v-model="deleteConfirmationModalOpen"
      :heading="$t('global.weeklyTimeScheduleDeleteConfirmationHeading')"
      :text="
        $t('global.weeklyTimeScheduleDeleteConfirmationText', {
          scheduleName: weeklyTimeSchedules[indexToDelete].name,
        })
      "
      :confirmText="$t('global.delete')"
      confirmBtnColor="error"
      :action="deleteWeeklyTimeSchedule"
    />
    <ConfirmationDialog
      v-if="isConfirmActivateModalOpen"
      v-model="isConfirmActivateModalOpen"
      :heading="$t('device.timeSchedule.confirmActivateModal.heading')"
      :text="
        $t('device.timeSchedule.confirmActivateModal.text', {
          scheduleName: weeklyTimeSchedules[indexToActivate].name,
        })
      "
      :confirmText="$t('global.activate')"
      :action="() => activateSchedule(indexToActivate, normalActivateCleanUp)"
    />
    <ConfirmationDialog
      v-if="isConfirmResetModalOpen"
      v-model="isConfirmResetModalOpen"
      :heading="$t('device.timeSchedule.reset.confirmation.title')"
      :text="$t('device.timeSchedule.reset.confirmation.text')"
      :confirmText="$t('global.reset')"
      confirmBtnColor="error"
      :action="confirmResetWeeklySchedules"
    />
  </v-dialog>
</template>
<script>
import DailyScheduleOverview from '@/components/TimeSchedules/DailyScheduleOverview'
import AddDailyScheduleButton from '@/components/TimeSchedules/AddDailyScheduleButton'
import SystemTimeScheduleEditValidationModal from '@/components/TimeSchedules/SystemTimeScheduleEditValidationModal'
import ConfirmationDialog from '@/components/ConfirmationDialog'
import { daysOfWeek } from '@/config/daysOfWeek'
import unsavedChangesUnloadGuard from '@/mixins/unsavedChangesUnloadGuard'
import isEqual from 'lodash/isEqual'
import Sortable from 'sortablejs'
import { validationMixin } from 'vuelidate'
import { required, maxLength } from 'vuelidate/lib/validators'
import { isUnique } from '@/utils/isUniqueValidator'
import { deviceSetActiveWeeklyTimeSchedule } from '@/graphql/mutations/deviceSetActiveWeeklyTimeSchedule'
import DailyTimeScheduleEditModal from '@/components/TimeSchedules/DailyTimeScheduleEditModal'
import produce from 'immer'

// Kept for openInViewMode prop compatibility
export const viewModeEnum = Object.freeze({
  VIEW: 'VIEW',
  EDIT: 'EDIT',
  NEW: 'NEW',
  NEW_WITH_ACTIVATE: 'NEW_WITH_ACTIVATE',
})

export default {
  name: 'WeeklyTimeSchedulesModal',
  mixins: [validationMixin, unsavedChangesUnloadGuard],
  components: {
    DailyScheduleOverview,
    ConfirmationDialog,
    AddDailyScheduleButton,
    DailyTimeScheduleEditModal,
    SystemTimeScheduleEditValidationModal,
  },
  model: {
    prop: 'isOpen',
    event: 'modalToggle',
  },
  props: {
    isOpen: Boolean,
    device: Object,
    weeklyTimeSchedules: Array,
    dailyTimeSchedules: Array,
    timeScheduleConfig: Object,
    createMethod: Function,
    updateMethod: Function,
    deleteMethod: Function,
    resetMethod: Function,
    dailyCreateMethod: Function,
    dailyUpdateMethod: Function,
    editable: Boolean,
    canActivate: Boolean,
    openInViewMode: String, // Uses values from viewModeEnum
    preselectScheduleId: String,
    activeTimeScheduleId: String,
    isActivatingTimeScheduleId: String,
    templateMode: Boolean,
  },
  data() {
    const initialSchedule =
      this.weeklyTimeSchedules && this.weeklyTimeSchedules.length > 0
        ? this.weeklyTimeSchedules[0]
        : null

    return {
      selectedWeeklyScheduleIndex:
        this.weeklyTimeSchedules && this.weeklyTimeSchedules.length > 0 ? 0 : null,
      actualTimeSchedule: initialSchedule
        ? initialSchedule.weeklySchedule.map((entry) => ({
            dailyTimeScheduleId: entry.dailySchedule.id,
            days: entry.days,
          }))
        : [],
      actualTimeScheduleName: initialSchedule ? initialSchedule.name : '',
      // Store the original state for comparison when editing
      originalTimeScheduleForComparison: null,
      originalTimeScheduleNameForComparison: '',
      operationMode: 'EDITING_EXISTING', // Default, will be set in isOpen watcher
      discardChangesConfirmationModalOpen: false,
      error: null,
      manualError: false,
      discardChangesAction: null,
      deleteConfirmationModalOpen: false,
      deleteNewConfirmationModalOpen: false,
      isConfirmResetModalOpen: false,
      isConfirmActivateModalOpen: false,
      indexToDelete: null,
      indexToActivate: null,
      mobileViewDetail: false,
      isReopeningAfterCloseOfDailyEditModal: false,
      isErrorModalOpen: false,
      isEditDailyDialogOpen: false,
      baseScheduleForDailyEditModal: null,
      dailyEditModalSaveMethod: null,
      namesTakenForDailyEditModal: null,
      dailyEditModalIsInUpdateMode: false,
      usedInWeeklyForDailyEditModal: null,
      systemTimeScheduleEditValidationModalOpen: false,
      systemDailyTimeScheduleEditValidationModalOpen: false,
      selectedDailyScheduleId: null,
      tooltipOn: null,
      tooltipAttrs: null,
      menuOn: null,
      menuAttrs: null,
    }
  },
  validations() {
    return {
      actualTimeScheduleName: {
        required,
        maxLength: maxLength(35),
        mustBeUnique: isUnique(
          this.operationMode === 'CREATING_NEW' ||
            this.operationMode === 'CREATING_NEW_AND_ACTIVATE'
            ? this.weeklySchedulesNames // All names are taken if creating new
            : this.selectedWeeklyScheduleIndex != null &&
              this.weeklyTimeSchedules[this.selectedWeeklyScheduleIndex]
            ? this.removeName(
                // Use original name for comparison if editing
                this.originalTimeScheduleNameForComparison,
                this.weeklySchedulesNames,
              )
            : this.weeklySchedulesNames, // Fallback, should ideally not happen in edit
        ),
      },
    }
  },
  methods: {
    _storeOriginalState() {
      if (
        this.operationMode === 'EDITING_EXISTING' &&
        this.selectedWeeklyScheduleIndex != null &&
        this.weeklyTimeSchedules[this.selectedWeeklyScheduleIndex]
      ) {
        const currentSchedule = this.weeklyTimeSchedules[this.selectedWeeklyScheduleIndex]
        this.originalTimeScheduleForComparison = currentSchedule.weeklySchedule.map((entry) => ({
          dailyTimeScheduleId: entry.dailySchedule.id,
          days: [...entry.days].sort(), // Ensure consistent order for comparison
        }))
        this.originalTimeScheduleNameForComparison = currentSchedule.name
      } else {
        // For CREATING_NEW or CREATING_NEW_AND_ACTIVATE
        // If it's a duplicate, the "original" is the state it was duplicated from.
        // If it's brand new, the "original" is empty.
        this.originalTimeScheduleForComparison =
          this.operationMode === 'CREATING_NEW' ||
          this.operationMode === 'CREATING_NEW_AND_ACTIVATE'
            ? [] // A truly new schedule starts empty
            : null // Should be set by startCreatingNewSchedule if duplicating
        this.originalTimeScheduleNameForComparison =
          this.operationMode === 'CREATING_NEW' ||
          this.operationMode === 'CREATING_NEW_AND_ACTIVATE'
            ? ''
            : '' // Should be set by startCreatingNewSchedule if duplicating
      }
      // If startCreatingNewSchedule with duplication, it will set actual values,
      // and _isDirty will compare against these as "original" for further edits.
      if (
        (this.operationMode === 'CREATING_NEW' ||
          this.operationMode === 'CREATING_NEW_AND_ACTIVATE') &&
        this.selectedWeeklyScheduleIndex == null // Indicates it's not based on an existing edit
      ) {
        // If we just duplicated, actualTimeSchedule/Name hold the duplicated values
        // These become the "original" for this new editing session
        this.originalTimeScheduleForComparison = JSON.parse(
          JSON.stringify(this.actualTimeSchedule),
        ).map((entry) => ({
          ...entry,
          days: [...entry.days].sort(),
        }))
        this.originalTimeScheduleNameForComparison = this.actualTimeScheduleName
      }
    },
    handleDailyScheduleClick(dailyTimeScheduleId) {
      if (this.editable) {
        const dailySchedule = this.dailyTimeSchedules.find(
          (schedule) => schedule.id === dailyTimeScheduleId,
        )
        if (!dailySchedule) return

        if (dailySchedule.isGlobal && !this.templateMode) {
          this.selectedDailyScheduleId = dailyTimeScheduleId
          this.systemDailyTimeScheduleEditValidationModalOpen = true
        } else {
          this.editDailySchedule(dailyTimeScheduleId)
        }
      }
    },
    _performDirectEditSchedule(index, weeklyTimeSchedule) {
      this.selectedWeeklyScheduleIndex = index
      this.actualTimeSchedule = weeklyTimeSchedule.weeklySchedule.map((entry) => ({
        dailyTimeScheduleId: entry.dailySchedule.id,
        days: [...entry.days].sort(),
      }))
      this.actualTimeScheduleName = weeklyTimeSchedule.name
      this.operationMode = 'EDITING_EXISTING'
      this._storeOriginalState()
      this.$v.actualTimeScheduleName.$reset()
      // It's correct to set this to false here, as we are loading a new, saved state
      this.$store.commit('setHasUnsavedChanges', false)
      this.mobileViewDetail = true
    },

    // New method to be called from the template
    tryDirectEditSchedule(index, weeklyTimeSchedule) {
      if (this.operationMode === 'EDITING_EXISTING' && this.selectedWeeklyScheduleIndex === index) {
        // Already editing this schedule, do nothing or perhaps focus first field.
        // For now, do nothing to prevent re-prompting if already on it.
        if (this.mobileView) this.mobileViewDetail = true // Ensure detail is visible on mobile
        return
      }

      if (this.hasUnsavedChanges) {
        this.discardChangesAction = () => {
          this._performDirectEditSchedule(index, weeklyTimeSchedule)
          this.discardChangesConfirmationModalOpen = false
        }
        this.discardChangesConfirmationModalOpen = true
      } else {
        this._performDirectEditSchedule(index, weeklyTimeSchedule)
      }
    },
    showErrorModal() {
      this.isErrorModalOpen = true
    },
    closeErrorModalAndRefresh() {
      this.$emit('modalToggle', false)
      this.$emit('isErrorInConfig')
    },
    setError(message) {
      this.manualError = true
      this.error = message
      setTimeout(() => {
        this.manualError = false
        if (this.error === message) {
          this.error = null
        }
      }, 3000)
    },
    async activateSchedule(indexToActivate, successAction) {
      try {
        await this.$apollo.mutate({
          mutation: deviceSetActiveWeeklyTimeSchedule,
          variables: {
            deviceId: this.device.id,
            weeklyTimeScheduleId: this.weeklyTimeSchedules[indexToActivate].id,
          },
        })
        this.$emit(
          'update:isActivatingTimeScheduleId',
          this.weeklyTimeSchedules[indexToActivate].id,
        )
        successAction()
      } catch (error) {
        console.error(error)
        if (
          error &&
          error.message &&
          error.message === 'DEVICE_SET_ACTIVE_WEEKLY_TIME_SCHEDULE_SCHEDULE_TOO_LARGE'
        ) {
          this.setError(this.$t('device.timeSchedule.setActiveTimeScheduleFailedTooLarge'))
        } else {
          this.setError(this.$t('device.timeSchedule.setActiveTimeScheduleFailed'))
        }
      }
    },
    async duplicateDailySchedule(dailyTimeScheduleId) {
      try {
        const dailySchedule = this.dailyTimeSchedules.find(
          (schedule) => schedule.id === dailyTimeScheduleId,
        )
        const newSchedule = produce(dailySchedule, (draft) => {
          draft.name = `${draft.name} ${this.$t('global.duplicateSchedulePostfix')}`
        })
        await this.createDailyAndAdd(newSchedule.schedule, newSchedule.name)
        this.setError(this.$t('global.duplicateDailyScheduleInfo'))
      } catch (error) {
        this.setError(this.$t('global.scheduleDuplicateFailed'))
      }
    },
    async createWeeklyTimeSchedule(successAction) {
      try {
        const createdSchedule = await this.createMethod(
          this.actualTimeSchedule,
          this.actualTimeScheduleName,
        )
        this.setError(this.$t('global.scheduleCreatedSuccess'))

        if (successAction) {
          successAction(createdSchedule) // Pass created schedule for activate
        } else {
          // After creation, find and switch to the new schedule
          // Parent component updates weeklyTimeSchedules, then we find it.
          this.$nextTick(() => {
            const newScheduleIndex = this.weeklyTimeSchedules.findIndex(
              (s) =>
                s.id === createdSchedule?.id || // If createMethod returns full obj
                s.name === this.actualTimeScheduleName, // Fallback
            )
            if (newScheduleIndex !== -1) {
              this.switchToSchedule(newScheduleIndex)
            } else if (this.weeklyTimeSchedules.length > 0) {
              this.switchToSchedule(0)
            }
            this.$store.commit('setHasUnsavedChanges', false)
          })
        }
      } catch (error) {
        this.setError(error.message || error)
      }
    },
    async updateWeeklyTimeSchedule(successAction) {
      try {
        await this.updateMethod(
          this.weeklyTimeSchedules[this.selectedWeeklyScheduleIndex].id,
          this.actualTimeSchedule,
          this.actualTimeScheduleName,
        )
        this.setError(this.$t('global.scheduleUpdatedSuccess'))
        this._storeOriginalState() // Re-store original state after successful save
        this.$v.actualTimeScheduleName.$reset() // Reset validation state after successful save
        this.$store.commit('setHasUnsavedChanges', false)

        if (successAction) {
          successAction()
        }
      } catch (error) {
        this.setError(error.message || error)
      }
    },
    async deleteWeeklyTimeSchedule() {
      try {
        const idToDelete = this.weeklyTimeSchedules[this.indexToDelete].id
        await this.deleteMethod(idToDelete)
        this.setError(this.$t('global.deleteWeeklyScheduleInfo'))

        this.$nextTick(() => {
          if (this.weeklyTimeSchedules.length === 0) {
            this.startCreatingNewSchedule()
          } else {
            let newIndexToSelect = 0
            if (
              this.selectedWeeklyScheduleIndex === this.indexToDelete ||
              this.selectedWeeklyScheduleIndex === null
            ) {
              newIndexToSelect = 0
            } else if (this.indexToDelete < this.selectedWeeklyScheduleIndex) {
              newIndexToSelect = this.selectedWeeklyScheduleIndex - 1
            } else {
              newIndexToSelect = this.selectedWeeklyScheduleIndex
            }
            this.switchToSchedule(
              Math.max(0, Math.min(newIndexToSelect, this.weeklyTimeSchedules.length - 1)),
            )
          }
        })

        this.deleteConfirmationModalOpen = false
        this.indexToDelete = null
      } catch (error) {
        this.setError(error.message || error)
      }
    },
    handleSave(successAction) {
      this.$v.actualTimeScheduleName.$touch()
      if (this.$v.actualTimeScheduleName.$invalid) {
        this.setError(this.$t('global.scheduleInvalidNameError'))
        return
      }
      if (
        this.disabledSaveMessage &&
        this.disabledSaveMessage !== this.$t('global.noChangeError')
      ) {
        // Allow save if only "noChangeError" but other validations pass (e.g. for save & activate)
        if (this.disabledSaveMessage !== this.$t('global.noChangeError') || !successAction) {
          this.setError(this.disabledSaveMessage)
          return
        }
      }

      if (this.operationMode === 'EDITING_EXISTING') {
        this.updateWeeklyTimeSchedule(successAction)
      } else if (
        this.operationMode === 'CREATING_NEW' ||
        this.operationMode === 'CREATING_NEW_AND_ACTIVATE'
      ) {
        this.createWeeklyTimeSchedule(successAction)
      }
    },
    handleSaveAndActivate() {
      this.handleSave((createdSchedule) => {
        this.$nextTick(() => {
          let indexToActivate = -1
          if (createdSchedule && createdSchedule.id) {
            indexToActivate = this.weeklyTimeSchedules.findIndex((s) => s.id === createdSchedule.id)
          }
          if (indexToActivate === -1) {
            indexToActivate = this.weeklyTimeSchedules.findIndex(
              (s) => s.name === this.actualTimeScheduleName, // schedule name might not be unique if backend assigns ID
            )
          }

          if (indexToActivate !== -1) {
            this.activateSchedule(indexToActivate, () => {
              this.normalActivateCleanUp()
              this.$emit('modalToggle', false)
            })
          } else {
            // If somehow not found, still close modal as save was attempted
            this.setError(this.$t('device.timeSchedule.setActiveTimeScheduleFailed')) // Or a more specific error
            this.$emit('modalToggle', false)
          }
        })
      })
    },
    normalActivateCleanUp() {
      this.isConfirmActivateModalOpen = false
      this.indexToActivate = null
    },
    openActivateConfirmation(index) {
      this.indexToActivate = index
      this.isConfirmActivateModalOpen = true
    },
    openDeleteConfirmationModal(index) {
      this.indexToDelete = index
      this.deleteConfirmationModalOpen = true
    },
    promptDiscardCurrentChanges() {
      this.discardChangesAction = () => {
        this.discardCurrentChanges()
        this.discardChangesConfirmationModalOpen = false
      }
      this.discardChangesConfirmationModalOpen = true
    },
    discardCurrentChanges() {
      if (
        this.operationMode === 'EDITING_EXISTING' &&
        this.selectedWeeklyScheduleIndex != null &&
        this.weeklyTimeSchedules[this.selectedWeeklyScheduleIndex]
      ) {
        this.actualTimeSchedule = JSON.parse(
          JSON.stringify(this.originalTimeScheduleForComparison),
        ).map((entry) => ({
          ...entry,
          days: [...entry.days].sort(),
        }))
        this.actualTimeScheduleName = this.originalTimeScheduleNameForComparison
        this.$v.actualTimeScheduleName.$reset()
        this.$store.commit('setHasUnsavedChanges', false)
      } else if (
        this.operationMode === 'CREATING_NEW' ||
        this.operationMode === 'CREATING_NEW_AND_ACTIVATE'
      ) {
        // Revert to the state it was when "New" or "Duplicate" was initiated
        this.actualTimeSchedule = JSON.parse(
          JSON.stringify(this.originalTimeScheduleForComparison || []),
        ).map((entry) => ({
          ...entry,
          days: [...entry.days].sort(),
        }))
        this.actualTimeScheduleName = this.originalTimeScheduleNameForComparison || ''
        this.$v.actualTimeScheduleName.$reset()
        this.$store.commit('setHasUnsavedChanges', false)
      }
    },
    tryGoBackToMenu() {
      if (this.hasUnsavedChanges) {
        // The action when "Discard Changes" is confirmed in the dialog
        this.discardChangesAction = () => {
          this.mobileViewDetail = false // Hide the detail view
          this.discardChangesConfirmationModalOpen = false // Close the confirmation dialog
          // Note: The unsaved data in actualTimeSchedule/Name remains.
          // If the user navigates back to this item, they'll see their uncommitted changes.
          // This is standard behavior for "discard and navigate away".
        }
        this.discardChangesConfirmationModalOpen = true // Show the "Unsaved Changes" dialog
      } else {
        this.mobileViewDetail = false // No unsaved changes, just hide detail view
      }
    },
    switchToSchedule(index) {
      if (index != null && this.weeklyTimeSchedules && this.weeklyTimeSchedules[index]) {
        this.selectedWeeklyScheduleIndex = index
        const schedule = this.weeklyTimeSchedules[index]
        this.actualTimeSchedule = schedule.weeklySchedule.map((entry) => ({
          dailyTimeScheduleId: entry.dailySchedule.id,
          days: [...entry.days].sort(),
        }))
        this.actualTimeScheduleName = schedule.name
        this.operationMode = 'EDITING_EXISTING'
      } else if (this.weeklyTimeSchedules && this.weeklyTimeSchedules.length > 0) {
        this.selectedWeeklyScheduleIndex = 0
        const schedule = this.weeklyTimeSchedules[0]
        this.actualTimeSchedule = schedule.weeklySchedule.map((entry) => ({
          dailyTimeScheduleId: entry.dailySchedule.id,
          days: [...entry.days].sort(),
        }))
        this.actualTimeScheduleName = schedule.name
        this.operationMode = 'EDITING_EXISTING'
      } else {
        this.startCreatingNewSchedule() // This sets operationMode and handles originals
        return
      }
      this._storeOriginalState()
      this.$v.actualTimeScheduleName.$reset()
      this.$store.commit('setHasUnsavedChanges', false)
      this.discardChangesAction = null
      this.discardChangesConfirmationModalOpen = false
    },
    tryClose() {
      if (this.hasUnsavedChanges) {
        // The action when "Discard Changes" is confirmed in the dialog
        this.discardChangesAction = () => {
          this.$emit('modalToggle', false) // Close the main modal
          this.discardChangesConfirmationModalOpen = false // Close the confirmation dialog
        }
        this.discardChangesConfirmationModalOpen = true // Show the "Unsaved Changes" dialog
      } else {
        this.$emit('modalToggle', false) // No unsaved changes, just close
      }
    },
    addDailySchedule(dailyTimeScheduleId) {
      const usedDays = this.actualTimeSchedule.map((schedule) => schedule.days).flat()
      const unusedDaysOfWeek = [...daysOfWeek].filter((day) => !usedDays.includes(day))
      this.actualTimeSchedule = [
        ...this.actualTimeSchedule,
        {
          dailyTimeScheduleId,
          days: unusedDaysOfWeek.sort(),
        },
      ]
    },
    removeDailySchedule(dailyTimeScheduleId) {
      this.setError(this.$t('global.deleteDailyScheduleInfo'))
      const indexToRemove = this.actualTimeSchedule.findIndex(
        (entry) => entry.dailyTimeScheduleId === dailyTimeScheduleId,
      )
      if (indexToRemove > -1) {
        this.actualTimeSchedule = [
          ...this.actualTimeSchedule.slice(0, indexToRemove),
          ...this.actualTimeSchedule.slice(indexToRemove + 1),
        ]
      }
    },
    editDailySchedule(dailyTimeScheduleId) {
      const dailyTimeSchedule = this.dailyTimeSchedules.find(
        (schedule) => schedule.id === dailyTimeScheduleId,
      )
      if (!dailyTimeSchedule) return

      this.baseScheduleForDailyEditModal = produce(dailyTimeSchedule, (draft) => {
        if (dailyTimeSchedule.isGlobal && !this.templateMode) {
          draft.name = `${draft.name} ${this.$t('global.duplicateSchedulePostfix')}`
        }
      })
      this.dailyEditModalIsInUpdateMode = this.templateMode || !dailyTimeSchedule.isGlobal
      this.dailyEditModalSaveMethod =
        dailyTimeSchedule.isGlobal && !this.templateMode
          ? this.createDailyAndSwap
          : this.dailyUpdateMethod
      this.namesTakenForDailyEditModal = this.dailyTimeSchedules
        .filter(
          (schedule) =>
            (!schedule.isGlobal || this.templateMode) && schedule.id !== dailyTimeScheduleId,
        )
        .map((schedule) => schedule.name)
      this.usedInWeeklyForDailyEditModal = this.weeklyTimeSchedules
        .filter((weeklySchedule) =>
          weeklySchedule.weeklySchedule.find(
            (entry) => entry.dailySchedule.id === dailyTimeScheduleId,
          ),
        )
        .map((weeklySchedule) => weeklySchedule.name)

      this.isReopeningAfterCloseOfDailyEditModal = true
      this.isEditDailyDialogOpen = true
    },
    createNewDailySchedule() {
      this.baseScheduleForDailyEditModal = {
        name: '',
        schedule: Object.fromEntries(
          this.timeScheduleConfig.schedules.map((scheduleConfig) => [scheduleConfig.name, []]),
        ),
      }
      this.dailyEditModalIsInUpdateMode = false
      this.dailyEditModalSaveMethod = this.createDailyAndAdd
      this.namesTakenForDailyEditModal = this.dailyTimeSchedules
        .filter((schedule) => !schedule.isGlobal || this.templateMode)
        .map((schedule) => schedule.name)

      this.isReopeningAfterCloseOfDailyEditModal = true
      this.isEditDailyDialogOpen = true
    },
    async createDailyAndSwap(newSchedule, newName) {
      try {
        const createdId = await this.dailyCreateMethod(newSchedule, newName)
        this.actualTimeSchedule = produce(this.actualTimeSchedule, (draft) => {
          const entryToChange = draft.find(
            (entry) => entry.dailyTimeScheduleId === this.baseScheduleForDailyEditModal.id,
          )
          if (entryToChange) {
            entryToChange.dailyTimeScheduleId = createdId
          }
        })
      } catch (error) {
        throw error
      }
    },
    async createDailyAndAdd(newSchedule, newName) {
      try {
        const createdId = await this.dailyCreateMethod(newSchedule, newName)
        this.addDailySchedule(createdId)
      } catch (error) {
        throw error
      }
    },
    computeNextValue(changedScheduleId, day) {
      const newValue = !this.actualTimeSchedule
        .find((entry) => entry.dailyTimeScheduleId === changedScheduleId)
        .days.includes(day)
      this.actualTimeSchedule = this.actualTimeSchedule.map((entry) => {
        const newEntry = { dailyTimeScheduleId: entry.dailyTimeScheduleId }
        if (entry.dailyTimeScheduleId === changedScheduleId) {
          if (newValue) {
            newEntry.days = [...entry.days, day]
          } else {
            newEntry.days = entry.days.filter((weekDay) => weekDay !== day)
          }
        } else {
          if (newValue) {
            newEntry.days = entry.days.filter((weekDay) => weekDay !== day)
          } else {
            newEntry.days = [...entry.days]
          }
        }
        newEntry.days.sort((a, b) => daysOfWeek.indexOf(a) - daysOfWeek.indexOf(b))
        return newEntry
      })
    },
    selectAllDays(changedScheduleId) {
      this.actualTimeSchedule = this.actualTimeSchedule.map((entry) => {
        const newEntry = { dailyTimeScheduleId: entry.dailyTimeScheduleId }
        if (entry.dailyTimeScheduleId === changedScheduleId) {
          newEntry.days = [...daysOfWeek]
        } else {
          newEntry.days = []
        }
        return newEntry
      })
    },
    saveOrder(event) {
      this.actualTimeSchedule.splice(
        event.newIndex,
        0,
        this.actualTimeSchedule.splice(event.oldIndex, 1)[0],
      )
    },
    tryStartCreatingNewSchedule(duplicateIndex) {
      if (this.hasUnsavedChanges) {
        this.discardChangesAction = () => {
          // User confirmed discarding current changes
          this.startCreatingNewSchedule(duplicateIndex)
          this.discardChangesConfirmationModalOpen = false
        }
        this.discardChangesConfirmationModalOpen = true
      } else {
        // No unsaved changes, proceed directly
        this.startCreatingNewSchedule(duplicateIndex)
      }
    },
    startCreatingNewSchedule(duplicateIndex) {
      this.$store.commit('setHasUnsavedChanges', false) // Reset for new form
      this.selectedWeeklyScheduleIndex = null
      this.operationMode = 'CREATING_NEW'

      if (duplicateIndex != null && this.weeklyTimeSchedules[duplicateIndex]) {
        this.setError(this.$t('global.duplicateWeeklyScheduleInfo'))
        this.actualTimeScheduleName = `${this.weeklyTimeSchedules[duplicateIndex].name} ${this.$t(
          'global.duplicateSchedulePostfix',
        )}`
        this.actualTimeSchedule = this.weeklyTimeSchedules[duplicateIndex].weeklySchedule.map(
          (entry) => ({
            dailyTimeScheduleId: entry.dailySchedule.id,
            days: [...entry.days].sort(),
          }),
        )
      } else {
        this.actualTimeScheduleName = ''
        this.actualTimeSchedule = []
      }
      this._storeOriginalState() // Store this new state as "original" for _isDirty
      this.$v.actualTimeScheduleName.$reset()
      this.mobileViewDetail = true
      // Watchers will determine if hasUnsavedChanges should be true based on _isDirty
    },
    removeName(name, namesTaken) {
      const index = namesTaken.indexOf(name)
      if (index === -1) {
        return namesTaken
      }
      const namesCopy = [...namesTaken]
      namesCopy.splice(index, 1)
      return namesCopy
    },
    deleteClickHandler() {
      if (
        this.operationMode === 'CREATING_NEW' ||
        this.operationMode === 'CREATING_NEW_AND_ACTIVATE'
      ) {
        this.deleteNewConfirmationModalOpen = true
      } else if (this.selectedWeeklyScheduleIndex != null) {
        this.indexToDelete = this.selectedWeeklyScheduleIndex
        this.deleteConfirmationModalOpen = true
      }
    },
    resetWeeklySchedules() {
      this.isConfirmResetModalOpen = true
    },
    async confirmResetWeeklySchedules() {
      try {
        const device = await this.resetMethod()

        if (device) {
          if (this.weeklyTimeSchedules.length === 0) {
            this.startCreatingNewSchedule()
          } else {
            let newIndexToSelect = 0
            if (
              this.selectedWeeklyScheduleIndex === this.indexToDelete ||
              this.selectedWeeklyScheduleIndex === null
            ) {
              newIndexToSelect = 0
            } else if (this.indexToDelete < this.selectedWeeklyScheduleIndex) {
              newIndexToSelect = this.selectedWeeklyScheduleIndex - 1
            } else {
              newIndexToSelect = this.selectedWeeklyScheduleIndex
            }
            this.switchToSchedule(
              Math.max(0, Math.min(newIndexToSelect, this.weeklyTimeSchedules.length - 1)),
            )
          }
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.isConfirmResetModalOpen = false
      }
    },
  },
  mounted() {
    console.log('WeeklyTimeSchedulesModal mounted - device:', this.device)
    console.log('WeeklyTimeSchedulesModal mounted - device.type:', this.device?.type)
  },
  computed: {
    _isDirty() {
      const nameChanged = this.actualTimeScheduleName !== this.originalTimeScheduleNameForComparison
      const scheduleContentChanged = !isEqual(
        this.actualTimeSchedule.map((e) => ({
          ...e,
          days: [...e.days].sort(),
        })), // Ensure days are sorted for comparison
        this.originalTimeScheduleForComparison.map((e) => ({
          ...e,
          days: [...e.days].sort(),
        })),
      )
      return nameChanged || scheduleContentChanged
    },
    mobileView() {
      return this.$vuetify.breakpoint.smAndDown
    },
    disabledDeleteMessage() {
      if (
        this.operationMode === 'CREATING_NEW' ||
        this.operationMode === 'CREATING_NEW_AND_ACTIVATE'
      ) {
        return null
      }
      if (
        this.selectedWeeklyScheduleIndex == null ||
        !this.weeklyTimeSchedules[this.selectedWeeklyScheduleIndex]
      ) {
        return null
      }
      if (this.templateMode && this.weeklyTimeSchedules[this.selectedWeeklyScheduleIndex].isUsed) {
        return this.$t('global.disabledDeleteTooltipForWeeklyUsed')
      }
      if (
        this.weeklyTimeSchedules[this.selectedWeeklyScheduleIndex].id === this.activeTimeScheduleId
      ) {
        return this.$t('global.disabledDeleteTooltipForWeeklyActive')
      }
      return null
    },
    dailyScheduleMap() {
      const map = new Map()
      this.dailyTimeSchedules.forEach((schedule) => {
        map.set(schedule.id, schedule)
      })
      return map
    },
    daysOfWeekItems() {
      return daysOfWeek.map((day) => ({
        value: day,
        text: this.$t(`global.daysOfWeek.${day}`),
      }))
    },
    canAddMoreDailySchedules() {
      return this.actualTimeSchedule.length < 7
    },
    weeklySchedulesNames() {
      if (!this.weeklyTimeSchedules) {
        return []
      }
      return this.weeklyTimeSchedules
        .filter((schedule) => !schedule.isGlobal || this.templateMode)
        .map((schedule) => schedule.name)
    },
    timeScheduleNameErrors() {
      const errors = []
      !this.$v.actualTimeScheduleName.required &&
        errors.push(this.$t('global.formValidation.required'))
      !this.$v.actualTimeScheduleName.maxLength &&
        errors.push(this.$t('global.formValidation.tooLong'))
      !this.$v.actualTimeScheduleName.mustBeUnique && errors.push(this.$t('global.nameTakenError'))
      return errors
    },
    visibleTimeScheduleNameErrors() {
      if (!this.$v.actualTimeScheduleName.$dirty) {
        return []
      } else {
        return this.timeScheduleNameErrors
      }
    },
    disabledSaveMessage() {
      if (!this.actualTimeSchedule.length) {
        return this.$t('global.weeklyScheduleEmptyError')
      }
      if (this.actualTimeSchedule.find((entry) => !entry.days.length)) {
        return this.$t('global.dailyScheduleNotUsedError')
      }
      const usedDays = this.actualTimeSchedule.flatMap((entry) => entry.days)
      if (usedDays.length !== 7) {
        const unusedDays = daysOfWeek.filter((day) => !usedDays.includes(day))
        return `${this.$t('global.everyDayHasToBeUsedError')} ${unusedDays
          .map((day) => this.$t(`global.daysOfWeek.${day}`))
          .join(', ')}`
      }
      if (this.timeScheduleNameErrors.length) {
        return this.$t('global.scheduleInvalidNameError')
      }
      if (!this.hasUnsavedChanges) {
        return this.$t('global.noChangeError')
      }
      return null
    },
  },
  watch: {
    actualTimeSchedule: {
      deep: true,
      handler() {
        this.$store.commit('setHasUnsavedChanges', this._isDirty)

        if (this.manualError) {
          return
        }
        if (!this.actualTimeSchedule.length) {
          this.error = this.$t('global.weeklyScheduleEmptyError')
        } else if (this.actualTimeSchedule.find((entry) => !entry.days.length)) {
          this.error = this.$t('global.dailyScheduleNotUsedError')
        } else {
          const usedDays = this.actualTimeSchedule.flatMap((entry) => entry.days)
          if (usedDays.length !== 7) {
            const unusedDays = daysOfWeek.filter((day) => !usedDays.includes(day))
            this.error = `${this.$t('global.everyDayHasToBeUsedError')} ${unusedDays
              .map((day) => this.$t(`global.daysOfWeek.${day}`))
              .join(', ')}`
          } else {
            this.error = null
          }
        }
      },
    },
    actualTimeScheduleName() {
      this.$store.commit('setHasUnsavedChanges', this._isDirty)
    },
    isOpen: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) {
          this.$store.commit('setHasUnsavedChanges', false)
          if (!this.isReopeningAfterCloseOfDailyEditModal) {
            const preselectedIndex = this.weeklyTimeSchedules.findIndex(
              (schedule) => schedule.id === this.preselectScheduleId,
            )

            if (this.openInViewMode === viewModeEnum.NEW) {
              this.startCreatingNewSchedule(preselectedIndex === -1 ? null : preselectedIndex)
            } else if (this.openInViewMode === viewModeEnum.NEW_WITH_ACTIVATE) {
              this.startCreatingNewSchedule(preselectedIndex === -1 ? null : preselectedIndex)
              this.operationMode = 'CREATING_NEW_AND_ACTIVATE' // Override if specifically for activate
            } else {
              this.mobileViewDetail = false
              const initialIndex = preselectedIndex === -1 ? 0 : preselectedIndex
              if (
                this.weeklyTimeSchedules &&
                this.weeklyTimeSchedules.length > 0 &&
                this.weeklyTimeSchedules[initialIndex]
              ) {
                this.switchToSchedule(initialIndex)
              } else {
                this.startCreatingNewSchedule()
              }
            }

            this.$nextTick(() => {
              if (this.$refs.sortableDailySchedules) {
                Sortable.create(this.$refs.sortableDailySchedules, {
                  animation: 150,
                  handle: '.sort-handle',
                  onUpdate: this.saveOrder,
                })
              }
            })
          }
          this.isReopeningAfterCloseOfDailyEditModal = false
        } else {
          this.$store.commit('setHasUnsavedChanges', false)
        }
      },
    },
    isEditDailyDialogOpen(isOpen) {
      if (!isOpen) {
        this.$nextTick(() => {
          if (this.baseScheduleForDailyEditModal) {
            // Assume daily modal might have made changes, re-evaluate dirty state
            this.$store.commit('setHasUnsavedChanges', this._isDirty)
          }
        })
        this.$emit('modalToggle', true)
      } else {
        this.$emit('modalToggle', false)
      }
    },
  },
}
</script>
<style lang="less" scoped>
@import '~@/assets/less/variables';
@import '~@/components/TimeSchedules/timeSchedule.less';

/deep/.weekly-time-schedules-modal {
  height: 825px;
  overflow: visible;
  border-radius: 16px;
  position: relative;
}

/deep/.weekly-time-schedules-modal-mobile {
  position: relative;

  .sidebar {
    flex-grow: 1;
  }

  .close-btn {
    top: 3px;
    right: 3px;
  }

  .back-btn {
    position: absolute;
    top: 3px;
    left: 3px;
  }

  .sidebar .schedule-list .action-buttons.always-visible {
    display: flex;
  }
}

.error-modal-content {
  background-color: white;
  padding: 20px;
  text-align: center;
}

.error-modal-content p {
  margin-bottom: 20px;
  font-size: 16px;
  color: #333;
}

.error-modal-content .v-btn {
  color: white;
}

.modal-content-wrapper {
  display: flex;
  height: 100%;
}

.sidebar {
  background: #eee;
  height: 100%;
  padding: 32px;
  display: flex;
  flex-direction: column;
  border-top-left-radius: 16px;
  border-bottom-left-radius: 16px;
  width: 350px;

  h1 {
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 24px;
  }

  .schedule-list {
    display: flex;
    flex-direction: column;
    gap: 5px;
    overflow: auto;
    flex-grow: 1;

    .schedule-selector-btn {
      padding: 17px 15px 17px 21px;
      border-radius: 8px;
      text-align: left;
      display: flex;
      align-items: center;
      justify-content: space-between;
      transition: background-color 0.2s ease;

      &.cursor-pointer {
        cursor: pointer;
      }

      /* Add cursor pointer to indicate clickability */
      &.editable {
        // This class might be less relevant now, use .cursor-pointer
        cursor: pointer;

        /* Optional: Add a subtle hover effect to indicate it's clickable */
        &:hover {
          background-color: #f5f5f5;
        }
      }

      .schedule-selector-btn-texts {
        overflow: hidden;
      }

      h2 {
        font-weight: 500;
        font-size: 14px;
        line-height: 16px;
        margin-bottom: 5px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      p {
        font-weight: 400;
        font-size: 13px;
        line-height: 15px;
        opacity: 0.4;
        margin-bottom: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .active-icon {
        width: 18px;
        height: 18px;
        border-radius: 100%;
        background: rgba(76, 175, 80, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
      }

      &.without-action-btns {
        padding: 17px 21px;
      }

      &.active {
        background: white;
      }

      &:hover:not(.active) {
        background: #e4e4e4;
      }

      .action-buttons {
        display: none;
      }

      &:hover {
        .active-icon {
          display: none;
        }

        .action-buttons {
          display: flex;
        }
      }
    }
  }

  .create-btn-wrapper {
    flex-wrap: wrap;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 32px;
    gap: 8px;
  }
}

.weekly-schedule-detail-wrapper {
  background: white;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  border-top-right-radius: 16px;
  border-bottom-right-radius: 16px;

  h1 {
    font-weight: 600;
    font-size: 16px;
    line-height: 19px;
  }

  .daily-schedules {
    overflow: auto;
  }

  .daily-entry-wrapper {
    border: 1px solid #e3e2e4;
    border-radius: 8px;
    margin-bottom: 18px;

    &.editable-daily {
      cursor: pointer;

      &:hover {
        background-color: rgba(0, 0, 0, 0.03);
      }
    }
    &.has-error {
      border-color: @color-error;
    }

    .sort-handle {
      cursor: move;
      margin-left: -9px;
      margin-right: 20px;
    }

    .entry-header {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
      padding: 0px 18px;
      margin-top: 17px;
    }

    h1 {
      font-weight: 600;
      font-size: 16px;
      line-height: 19px;
    }

    .repeat-span {
      font-size: 14px;
    }

    .repeated-days-list {
      font-size: 14px;
      font-weight: 500;
    }

    .repeat-with-error {
      color: @color-error;

      /deep/i {
        color: @color-error;
      }
    }
  }

  footer {
    display: flex;
    border-top: 1px solid #e8e8e8;

    .actions {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
    }
  }
}

.day-of-week-list-item {
  /deep/.v-input--checkbox {
    margin-top: 0;
    padding-top: 0;
  }
}

.close-btn {
  position: absolute;
  top: -39px;
  right: -8px;
}
</style>
